/**
 * Subdomain-based tenant resolution for NOIZA.
 *
 * Supports URLs like:
 *   fusion-tarrazu.noiza.estrategiasdigitalesaz.com  → slug = "fusion-tarrazu"
 *   noiza.estrategiasdigitalesaz.com/fusion-tarrazu   → slug from path (fallback)
 *   localhost:3000                                     → no subdomain (dev)
 *
 * Does NOT break existing path-based routing.
 */

// Known base domains where the app is hosted.
// Subdomains of these are treated as tenant slugs.
const BASE_DOMAINS = [
  'noizacr.com',
  'noiza.estrategiasdigitalesaz.com',
  'noiza.vercel.app', // if deployed on Vercel
]

/**
 * Extract a tenant slug from the current hostname's subdomain.
 *
 * Returns the slug string if a valid tenant subdomain is detected,
 * or `null` if we're on the bare base domain (or localhost).
 *
 * Examples:
 *   "fusion-tarrazu.noiza.estrategiasdigitalesaz.com" → "fusion-tarrazu"
 *   "noiza.estrategiasdigitalesaz.com"                 → null
 *   "localhost"                                         → null
 *   "127.0.0.1"                                        → null
 */
export function getSubdomainSlug(): string | null {
  const hostname = window.location.hostname.toLowerCase()

  // Skip localhost / IP addresses — no subdomain resolution in dev
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.endsWith('.localhost')
  ) {
    return null
  }

  // Check each known base domain
  for (const base of BASE_DOMAINS) {
    if (hostname === base) {
      // Exact match — no subdomain
      return null
    }

    if (hostname.endsWith(`.${base}`)) {
      // Extract everything before the base domain
      const subdomain = hostname.slice(0, hostname.length - base.length - 1)

      // Validate: must be a single-level subdomain (no dots), non-empty
      if (subdomain && !subdomain.includes('.')) {
        // Skip "www" and "admin" as they're not tenant slugs
        if (subdomain === 'www' || subdomain === 'admin') {
          return null
        }
        return subdomain
      }

      // Multi-level subdomain (e.g., "admin.fusion-tarrazu") — not a simple tenant slug
      return null
    }
  }

  // Unknown domain — could be a custom domain in the future
  // For now, return null (no subdomain resolution)
  return null
}

/**
 * Check if the current request is on a tenant subdomain.
 */
export function isSubdomainAccess(): boolean {
  return getSubdomainSlug() !== null
}

/**
 * Get the base URL for the current environment.
 * Useful for building links that go back to the main domain.
 */
export function getBaseUrl(): string {
  const hostname = window.location.hostname.toLowerCase()
  const protocol = window.location.protocol

  for (const base of BASE_DOMAINS) {
    if (hostname === base || hostname.endsWith(`.${base}`)) {
      return `${protocol}//${base}`
    }
  }

  // Fallback for dev
  return `${protocol}//${hostname}${window.location.port ? `:${window.location.port}` : ''}`
}

/**
 * Build a tenant-branded URL.
 * If subdomains are supported, returns subdomain URL; otherwise path-based.
 */
export function getTenantUrl(slug: string): string {
  const protocol = window.location.protocol

  for (const base of BASE_DOMAINS) {
    const hostname = window.location.hostname.toLowerCase()
    if (hostname === base || hostname.endsWith(`.${base}`)) {
      return `${protocol}//${slug}.${base}`
    }
  }

  // Dev fallback — use path-based
  return `/${slug}`
}
